import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Bot, Workflow, Code2, Smartphone, TrendingUp, ArrowUpRight } from 'lucide-react';

const services = [
  {
    n: '01',
    category: 'Autonomous Intelligence',
    title: 'AI Chatbots & Autonomous Agents',
    desc: 'Custom conversational agents trained on your proprietary data. Handles multi-step customer inquiries, lead qualification, and CRM updates around the clock.',
    image: 'https://images.unsplash.com/photo-1531482615713-2afd69097998?auto=format&fit=crop&w=1000&q=80',
    Icon: Bot,
    accent: '#0284C7',
    link: '/ai-chatbot',
    span: 'lg:col-span-7',
    aspect: 'aspect-[16/9] lg:aspect-[21/9]',
  },
  {
    n: '02',
    category: 'Operations',
    title: 'Workflow & Pipeline Automation',
    desc: 'Connect your fragmented software stack. We engineer automated data pipelines that eliminate manual tasks and run without human supervision.',
    image: 'https://images.unsplash.com/photo-1551836022-d5d88e9218df?auto=format&fit=crop&w=800&q=80',
    Icon: Workflow,
    accent: '#059669',
    link: '/automation',
    span: 'lg:col-span-5',
    aspect: 'aspect-[16/9] lg:aspect-[4/3]',
  },
  {
    n: '03',
    category: 'Cloud Software',
    title: 'SaaS Product Development',
    desc: 'Full-stack cloud applications built from scratch. From MVP to enterprise scale with React, Node, and multi-tenant security.',
    image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=800&q=80',
    Icon: Code2,
    accent: '#4F46E5',
    link: '/saas-development',
    span: 'lg:col-span-5',
    aspect: 'aspect-[16/9] lg:aspect-[4/3]',
  },
  {
    n: '04',
    category: 'Digital Products',
    title: 'Website & Mobile App Development',
    desc: 'High-speed websites and native iOS & Android applications. Built with modern UI design, lightning page speeds, and conversion-focused user UX.',
    image: 'https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?auto=format&fit=crop&w=1000&q=80',
    Icon: Smartphone,
    accent: '#D97706',
    link: '/saas-development',
    span: 'lg:col-span-7',
    aspect: 'aspect-[16/9] lg:aspect-[21/9]',
  },
  {
    n: '05',
    category: 'Search & Generative AI Optimization',
    title: 'SEO & GEO (Generative Engine Optimization)',
    desc: 'Technical search optimization to dominate Google rankings, plus Generative Engine Optimization (GEO) to get your brand recommended by ChatGPT, Perplexity, and AI search engines.',
    image: 'https://images.unsplash.com/photo-1504868584819-f8e8b4b6d7e3?auto=format&fit=crop&w=1200&q=80',
    Icon: TrendingUp,
    accent: '#0891B2',
    link: null,
    span: 'lg:col-span-12',
    aspect: 'aspect-[16/9] lg:aspect-[25/8]',
  },
];

const Services = () => {
  return (
    <section id="services" className="py-24 bg-[#F8FAFC] text-slate-900 relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">

        {/* Section Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
          <div>
            <div className="section-label mb-3">Core Engineering Disciplines</div>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 tracking-tight">
              Software capabilities <br className="hidden sm:block" />
              <span className="text-sky-600">built for business impact.</span>
            </h2>
          </div>
          <p className="text-slate-600 text-base max-w-md border-l border-slate-300 pl-4 font-normal">
            Every system is custom-architected around your exact business targets by our senior in-house engineering team.
          </p>
        </div>

        {/* Asymmetric Bento Grid on Light Surface */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {services.map((s, idx) => (
            <motion.div
              key={s.title}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ delay: idx * 0.08, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
              className={`bento-card-light group flex flex-col justify-between ${s.span}`}
            >
              {/* Image Frame with Overlay */}
              <div className={`relative w-full ${s.aspect} overflow-hidden`}>
                <img
                  src={s.image}
                  alt={s.title}
                  className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-700"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-white via-white/10 to-transparent opacity-85" />
                
                {/* Number Badge */}
                <div className="absolute top-4 right-4 font-mono text-xs font-bold text-slate-700 bg-white/90 backdrop-blur-md border border-slate-200 px-3 py-1 rounded-full shadow-sm">
                  {s.n}
                </div>
              </div>

              {/* Card Body Content */}
              <div className="p-6 sm:p-8 flex flex-col flex-1 justify-between bg-white relative z-10 -mt-6">
                <div>
                  <div className="flex items-center gap-3 mb-3">
                    <div
                      className="w-9 h-9 rounded-lg flex items-center justify-center border"
                      style={{
                        backgroundColor: `${s.accent}12`,
                        borderColor: `${s.accent}30`,
                        color: s.accent,
                      }}
                    >
                      <s.Icon size={18} strokeWidth={2} />
                    </div>
                    <span className="text-xs font-mono font-semibold uppercase tracking-wider text-slate-500">
                      {s.category}
                    </span>
                  </div>

                  <h3 className="font-heading text-xl sm:text-2xl font-bold text-slate-900 mb-3 group-hover:text-sky-600 transition-colors">
                    {s.title}
                  </h3>

                  <p className="text-slate-600 text-sm leading-relaxed mb-6 font-normal">
                    {s.desc}
                  </p>
                </div>

                {s.link ? (
                  <Link
                    to={s.link}
                    className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-sky-600 hover:text-slate-900 transition-colors w-fit pt-2 border-t border-slate-100"
                  >
                    Explore Discipline
                    <ArrowUpRight size={14} />
                  </Link>
                ) : (
                  <div className="pt-2 border-t border-slate-100 text-xs font-mono text-slate-500 uppercase tracking-wider">
                    Full Service Execution
                  </div>
                )}
              </div>
            </motion.div>
          ))}
        </div>

      </div>
    </section>
  );
};

export default Services;
